import { DocumentScrollingService } from './../document/document-scrolling.service';
import { EventEmitter, Injectable, Output, NgZone } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ThemeColorService } from '../ui/theme-color.service';
import { LightDarkThemes } from 'src/models/AppTheme';
import { AnnotationEvent, BottomBarEvent } from 'src/models/Events';
import { mapSelectedTextWithQuads, removeTronUI } from 'src/app/core/pdf-utils/events.handlers';
import { PaneService } from '../pane.service';
import { SettingsService } from '../settings/settings.service';
import { ReadingMode } from 'src/app/core/reading-utils/enums';
import { asyncScheduler, BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, throttleTime } from 'rxjs/operators';
import { AsgardDocPdf } from 'src/app/core/documents/asgard-doc-pdf';
import { DocumentHighlightService } from '../document/document-highlight.service';
import { ISettings, ViewerInstanceClickEv } from 'src/app/core/interfaces';
import { DocumentManagerService } from '../document/document-manager.service';
import { PdfAnnotationActions, PdfTools, AnnotationTypes, PdfFitModes, PdfViewerLayoutModes } from 'src/app/core/pdf-utils/enums';
import { ToastrDialogService } from '../toastr-dialog.service';
import { ToastrStringsEnum, ToastrActionEnum } from 'src/app/utils/enums';
import { isAnnotationType, filterAnnotationExceptHighlights } from 'src/app/core/common';
import { ReadingModesService } from '../reading-modes.service';
import { FileHashManager } from '../autosave/file-hash-manager';
import { AnnotationCommand } from '../command/pdf-commands/annotation.command';
import { CommandInvokerService } from '../command/command-invoker.service';
import { AutosaveManagerService } from '../autosave/autosave-manager.service';
import { AudioMakerService } from '../audio-maker/audio-maker.service';
import { CurrentExamService } from '../exam/current-exam.service';
import { detectHotkey, getHotkeyString } from 'src/app/utils/helpers/hotkey-combination.helper';
import { HotkeyEventSource, HotkeysService } from '../exam/hotkeys.service';
import WebViewer, { Core, WebViewerInstance, WebViewerOptions } from '@pdftron/webviewer';
import { convertPdfColourToName } from 'src/app/core/pdf-utils/markers-utils';
import { GoogleAnalyticsActions, GoogleAnalyticsService } from '../google-analytics.service';
import { TabService } from '../tab.service';


interface PdfViewerInstance {
  paneIndex: number;
  asgardDoc: AsgardDocPdf;
  pdfDoc: Core.PDFNet.PDFDoc | null;
  viewerAPI: WebViewerInstance;
  viewerListeners: { type: string, listener: (...args) => void }[] | null;
  instSubscr: Subscription | null;
}

let timeStart: number;

@Injectable({
  providedIn: 'root'
})
export class PdfInstanceManagerService {
  @Output() pageChanged: EventEmitter<BottomBarEvent | null> = new EventEmitter();
  @Output() pageCountChanged: EventEmitter<BottomBarEvent | null> = new EventEmitter();
  @Output() zoomChanged: EventEmitter<BottomBarEvent | null> = new EventEmitter();
  @Output() pagesUpdated: EventEmitter<BottomBarEvent | null> = new EventEmitter();
  @Output() fitModeChanged: EventEmitter<BottomBarEvent | null> = new EventEmitter();
  @Output() PandeIndexChanged: EventEmitter<BottomBarEvent | null> = new EventEmitter();
  @Output() annotationsLoaded: EventEmitter<AnnotationEvent<Core.Annotations.Annotation>> = new EventEmitter();
  @Output() freeHandAnnotationCreated: EventEmitter<AnnotationEvent<Core.Annotations.FreeHandAnnotation>> = new EventEmitter();
  @Output() freeHandAnnotationAdded: EventEmitter<AnnotationEvent<Core.Annotations.FreeHandAnnotation>> = new EventEmitter();
  @Output() annotationSelected: EventEmitter<AnnotationEvent<Core.Annotations.Annotation>> = new EventEmitter();
  @Output() annotationDeselected: EventEmitter<AnnotationEvent<Core.Annotations.Annotation>> = new EventEmitter();
  @Output() annotationAdded: EventEmitter<AnnotationEvent<Core.Annotations.Annotation>> = new EventEmitter();
  @Output() annotationModified: EventEmitter<AnnotationEvent<Core.Annotations.Annotation>> = new EventEmitter();
  @Output() annotationDeleted: EventEmitter<AnnotationEvent<Core.Annotations.Annotation>> = new EventEmitter();
  @Output() annotationHovered: EventEmitter<AnnotationEvent<Core.Annotations.Annotation>> = new EventEmitter();
  @Output() annotationDoubleClicked: EventEmitter<AnnotationEvent<Core.Annotations.Annotation>> = new EventEmitter();
  @Output() mouseLeftDown: EventEmitter<void> = new EventEmitter();
  @Output() mouseLeftUp: EventEmitter<void> = new EventEmitter();
  @Output() mouseClicked: EventEmitter<ViewerInstanceClickEv> = new EventEmitter();
  @Output() documentScrolled: EventEmitter<Event> = new EventEmitter();
  @Output() wordHovered: EventEmitter<MouseEvent> = new EventEmitter();
  @Output() mouseInAndMoving: EventEmitter<void> = new EventEmitter();
  @Output() documentLoaded: EventEmitter<string> = new EventEmitter();

  private readonly instanceStorage: Map<string, PdfViewerInstance> = new Map();

  private mouseIn: boolean = false;
  private leftPaneInstanceStylesPath: string = '../../styles/pdftron-custom-styles-left-pane.css';
  private rightPaneInstanceStylesPath: string = '../../styles/pdftron-custom-styles-right-pane.css';
  private prevSelectedText: BehaviorSubject<any | null> = new BehaviorSubject(null);
  private mouseHoverText: BehaviorSubject<any | null> = new BehaviorSubject(null);
  private mouseHoverTextNote: BehaviorSubject<Core.Annotations.Annotation | null> = new BehaviorSubject(null);
  private textNoteMoving: BehaviorSubject<Core.Annotations.Annotation | null> = new BehaviorSubject(null);

  private settings: ISettings | null = null;
  private selectedAnnots: Core.Annotations.Annotation[] | null = null;

  private fileUrl = '';
  private _isPDFNetInitialized = false;
  get isPDFNetInitialized(): boolean {
    return this._isPDFNetInitialized;
  }

  constructor(
    private readonly themeService: ThemeColorService,
    private readonly paneService: PaneService,
    private readonly settingsService: SettingsService,
    private readonly docHighlightSvc: DocumentHighlightService,
    private readonly documentScrollingService: DocumentScrollingService,
    private readonly docMgrService: DocumentManagerService,
    private readonly toastrService: ToastrDialogService,
    private readonly readingModeService: ReadingModesService,
    private readonly ngZone: NgZone,
    private readonly commandInvoker: CommandInvokerService,
    private readonly autosave: AutosaveManagerService,
    private readonly audioMakerService: AudioMakerService,
    private readonly examService: CurrentExamService,
    private readonly hotkeyService: HotkeysService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly tabService: TabService
  ) {
    this.initServiceSubscrs();
  }

  createViewerInstace(file: File, el: HTMLElement, doc: AsgardDocPdf, password: string, paneIndex: number): void {
    const isSTI = this.tabService.activeTab?.isSTI ?? false;
    // const paneIndex = this.paneService.getActivePaneIndex();
    const opts: WebViewerOptions = {
      path: '/lib/pdftron/',
      disabledElements: [
        'notesPanel', 'notesPanelButton', 'outlinesPanel', 'outlinesPanelButton',
        'contextMenuPopup', 'annotationStylePopup', 'textPopup', 'annotationGroupButton',
        'toolsOverlay', 'toolStylePopup', 'searchPanel', 'searchOverlay', 'viewControlsOverlay',
        'linkButton', 'toolsHeader', 'annotationContentOverlay',
        ...(isSTI ? ['extractPage'] : []) // NOTE 4 AUG 2026: sti files should not be allowed to extract pages
      ],
      fullAPI: true,
      enableOptimizedWorkers: true,
      licenseKey: environment.pdfTronLicenceKey,
      css: paneIndex === 0 ? this.leftPaneInstanceStylesPath : this.rightPaneInstanceStylesPath
    };

    WebViewer(opts, el)
      .then(async (instance: WebViewerInstance) => {
        const asgInst = {
          viewerAPI: instance, viewerListeners: null, instSubscr: null, asgardDoc: doc, pdfDoc: null, paneIndex, pageCanvases: [],
        };

        /* reduce delay for FreeHandCreateTool (https://www.pdftron.com/documentation/web/faq/freehand-creation-delay) */
        instance.Core.Tools.FreeHandCreateTool.prototype.createDelay = 500;

        /* force to not use PNaCl */
        const CoreControls = instance.Core;
        CoreControls.forceBackendType('ems');
        // CoreControls.enableSubzero(false);

        /* disable because of print status is  */
        instance.UI.useEmbeddedPrint(true);
        instance.UI.setPrintQuality(2);

        /* add events handlers to instances docviewers */
        this.addEventsListeners(asgInst, file, doc);
        // setInanceTheme(instance, this.themeService.getCurrentTheme());
        instance.UI.setTheme(this.themeService.getCurrentLightDarkTheme() === LightDarkThemes.Light ? 'light' : 'dark');
        removeTronUI(instance);

        const loadOptions = { password: '', extension: 'pdf' };
        if (password) {
          loadOptions.password = password;
        }

        timeStart = performance.now();
        // instance.loadDocument

        // NOTE 4 AUG 2026: 
        // disable features that lead to extraction of the document in case of STI files
        if (isSTI) {
          instance.UI.disableFeatures([
            instance.UI.Feature.Print
          ]);
        }

        // first part of pdftron workaround for support ticket
        // #25766 opening/getting data pdf issue
        this.fileUrl = URL.createObjectURL(file);
        instance.UI.loadDocument(this.fileUrl, loadOptions);
      })
      .catch(err => {
        console.log(err);
      });
  }

  cleanOndestroy(): void {
    this.instanceStorage.forEach((_value, key) => this.destroy(key));
    this.instanceStorage.clear();
  }

  getViewerInstance(documentId: string): WebViewerInstance | null {
    const asgInst = this.instanceStorage.get(documentId);
    if (asgInst) {
      return asgInst.viewerAPI;
    }
    return null;
  }

  getDocViewer(tabId: string): Core.DocumentViewer | null {
    const asgardTabDocViewer = this.getViewerInstance(tabId);
    if (asgardTabDocViewer) {
      return asgardTabDocViewer.Core.documentViewer;
    }
    return null;
  }

  setInstancesThemes(e: LightDarkThemes): void {
    this.instanceStorage.forEach((i: PdfViewerInstance) => {
      // setInanceTheme(i.viewerAPI, e);
      i.viewerAPI.UI.setTheme(e.toString());
    });
  }

  private async onInstanceReady(documentId: string): Promise<void> {
    const asgInst = this.instanceStorage.get(documentId);

    if (asgInst) {
      const docViewer = asgInst.viewerAPI.Core.documentViewer;
      const pdfDocUtil = await docViewer.getDocument().getPDFDoc();

      await asgInst.viewerAPI.Core.PDFNet.initialize();
      this._isPDFNetInitialized = true;
      asgInst.pdfDoc = pdfDocUtil;

      if (this.settings && this.settings.savedFitMode) {
        asgInst.viewerAPI.UI.setFitMode(this.settings.savedFitMode);
      }

      if (this.settings && this.settings.savedLayoutMode) {
        asgInst.viewerAPI.UI.setLayoutMode(this.settings.savedLayoutMode);
      }

      if (this.settings && this.settings.savedFitMode === PdfFitModes.Zoom && this.settings.savedZoom && this.settings.savedZoom > 0) {
        asgInst.viewerAPI.UI.setZoomLevel(this.settings.savedZoom / 100)
      }
    }
    const end = performance.now();

    console.log("File loading took " + (end - timeStart) + " milliseconds.")
  }

  private initInstanceSuscrs(documentId: string): void {
    const asgInst = this.instanceStorage.get(documentId);
    if (!asgInst) {
      return;
    }
    const docViewer = asgInst.viewerAPI.Core.documentViewer;

    asgInst.instSubscr = asgInst.asgardDoc.readingItemObservable.subscribe(
      () => this.docHighlightSvc.onReadingItemUpdated(documentId)
    );

    asgInst.instSubscr.add(
      fromEvent(docViewer.getScrollViewElement(), 'wheel')
        .subscribe(() => {
          if (this.docMgrService.isFocusDocument) {
            this.documentScrolled.emit();
          }
        })
    );

    asgInst.instSubscr.add(
      fromEvent(docViewer.getScrollViewElement(), 'scroll')
        .pipe(throttleTime(250, asyncScheduler, { leading: false, trailing: true }))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.documentScrollingService.updateShowedPageRect();
          });
        })
    );

    asgInst.instSubscr.add(
      this.textNoteMoving.asObservable()
        .pipe(throttleTime(250, asyncScheduler, { leading: false, trailing: true }))
        .subscribe(
          (e: Core.Annotations.Annotation) => {
            if (e) {
              this.annotationModified.emit({ tabId: documentId, annotations: [e] });
            }
          }
        )
    );

    asgInst.instSubscr.add(
      this.mouseHoverTextNote.asObservable()
        .pipe(throttleTime(50, asyncScheduler, { leading: false, trailing: true }))
        .subscribe(
          (e: Core.Annotations.Annotation) => {
            this.annotationHovered.emit({ tabId: documentId, annotations: [e] });
          }
        )
    );
  }

  private addEventsListeners(asgInst: PdfViewerInstance, file: File, doc: AsgardDocPdf): void {
    const documentId = asgInst.asgardDoc.documentId;
    const docViewer = asgInst.viewerAPI.Core.documentViewer;
    const annotManager = asgInst.viewerAPI.Core.annotationManager;

    /* apply current styles before load the doc */
    const bfCb = () => {
      this.setInstancesThemes(this.themeService.getCurrentLightDarkTheme());
    };
    docViewer.removeEventListener('beforeDocumentLoaded', bfCb);
    docViewer.addEventListener('beforeDocumentLoaded', bfCb);

    const docLoaded = async () => {
      /* calc hash */
      asgInst.asgardDoc.docHash = await FileHashManager.calculateFileHash(file);
      this.instanceStorage.set(doc.documentId, asgInst);
      this.documentLoaded.emit(doc.documentId);
      /* store initial file state */
      this.autosave.addOriginalFile(asgInst.asgardDoc.docHash, file, file.name, null, this.examService.getDocDocumentType(documentId));
      await this.onInstanceReady(documentId);

      // TODO: separate annotations/document stuff
      this.docMgrService.setDocumentOptins(documentId,
        [asgInst.viewerAPI, asgInst.pdfDoc, docViewer.getPageCount(), docViewer.getCurrentPage()]
      );

      // second part of pdftron workaround for support ticket
      // #25766 opening/getting data pdf issue
      if (this.fileUrl !== '') {
        URL.revokeObjectURL(this.fileUrl);
        this.fileUrl = '';
      }
    };
    docViewer.removeEventListener('documentLoaded', docLoaded);
    docViewer.addEventListener('documentLoaded', docLoaded);

    /* document annotations loaded */
    const annotationsLoaded = async () => {
      const end = performance.now();

      console.log(`Annotations was loaded with ${end - timeStart} miliseconds`);

      this.initInstanceSuscrs(documentId);

      // TODO: separate annotations/document stuff 
      // this.docMgrService.setDocumentOptins(documentId,
      //   [asgInst.viewerAPI, asgInst.pdfDoc, docViewer.getPageCount(), docViewer.getCurrentPage()]
      // );

      this.annotationsLoaded.emit({ tabId: documentId });

      freeHandAnnotationTool.addEventListener('annotationCreated', freeHandAnnotationCreated);
      freeHandAnnotationTool.addEventListener('annotationAdded', freeHandAnnotationAdded);
      annotManager.addEventListener('annotationSelected', annotationSelected);
      annotManager.addEventListener('annotationChanged', annotationChanged);

      annotManager.disableFreeTextEditing();

      // console.log('checkForSavedPageAndReadingPostion', documentId);
      await this.autosave.checkForSavedPageAndReadingPostion(documentId);

      if (this.settings) {
        asgInst.asgardDoc.handleCurrentNavigationMode(this.settings.pageNavigationMode);
      }
    };
    docViewer.removeEventListener('annotationsLoaded', annotationsLoaded);
    docViewer.addEventListener('annotationsLoaded', annotationsLoaded);

    /* track current page number */
    const pgChange = async (...args: any[]) => {

      this.pageChanged.emit({ value: args[0], tabId: documentId });
      const asgardDoc = this.docMgrService.getDocument(documentId);
      if (asgardDoc) {
        this.autosave.updateDocumentPage(asgardDoc.docHash, args[0]);
        (asgardDoc as AsgardDocPdf).setCurrentPageIdx(args[0]);
      }
    };
    docViewer.removeEventListener('pageNumberUpdated', pgChange);
    docViewer.addEventListener('pageNumberUpdated', pgChange);

    /* track current zoom ratio */
    const zmUpd = (...args: any[]) => {
      this.zoomChanged.emit({ value: args[0], tabId: documentId });
    };
    docViewer.removeEventListener('zoomUpdated', zmUpd);
    docViewer.addEventListener('zoomUpdated', zmUpd);

    /* cover, dual and single modes changes */
    const disUpd = () => {
      this.pagesUpdated.emit({ value: true, tabId: documentId });
    };
    docViewer.removeEventListener('displayModeUpdated', disUpd);
    docViewer.addEventListener('displayModeUpdated', disUpd);

    /*fit width, fit page changes */
    const ftUpd = (...args: any[]) => {
      this.fitModeChanged.emit({ value: args[0], tabId: documentId });
    };
    docViewer.removeEventListener('fitModeUpdated', ftUpd);
    docViewer.addEventListener('fitModeUpdated', ftUpd);

    /* delete, add, etc manipulation with document pages */
    const pgUpd = async (...args: any[]) => {
      this.pageCountChanged.emit({ value: true, tabId: documentId });
      if (args[0].added.length > 0 || args[0].removed.length > 0 || args[0].moved.length > 0) {
        const asgardDoc = this.docMgrService.getDocument(documentId);
        if (!asgardDoc || !asgardDoc.docHash) return;

        asgInst.asgardDoc.docHash = await FileHashManager.calculateFileHash(file);
        this.instanceStorage.set(doc.documentId, asgInst);

        const fileBuffer = await docViewer.getDocument().getFileData({});
        const blob = new Blob([fileBuffer], { type: file.type });
        const updatedFile = new File([blob], file.name, { type: file.type });
        this.autosave.updateOriginalFile(asgardDoc.docHash, updatedFile);
      }
    };
    // 6 FEB 2025
    // We renamed the layoutChanged API to pagesUpdated
    // https://community.apryse.com/t/docviewer-on-rotationupdated-not-triggering/5022/4
    docViewer.removeEventListener('pagesUpdated', pgUpd);
    docViewer.addEventListener('pagesUpdated', pgUpd);

    docViewer.addEventListener('pageComplete', (pageNum) => {
      const end = performance.now();

      console.log(`Page #${pageNum} was loaded with ${end - timeStart} miliseconds`);
    })

    const clickEv = (...args: any[]) => {
      if (args[0]) {
        if (!asgInst.asgardDoc.isDocumentReady) {
          this.ngZone.run(() => {
            this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.PrepareDoc, false);
          });

          return;
        }

        this.mouseClicked.emit({ ev: args[0], docId: documentId, pandeIndex: asgInst.paneIndex });
      }
    };

    docViewer.removeEventListener('click', clickEv);
    docViewer.addEventListener('click', clickEv);

    const dblClick = (...args: any[]) => {
      if (args[0]) {
        const anot = annotManager.getAnnotationByMouseEvent(args[0]);
        if (anot && isAnnotationType(anot, [AnnotationTypes.FreeText, AnnotationTypes.StickyNote], true)) {
          this.annotationDoubleClicked.emit({ tabId: documentId, annotations: [anot] });
        }
      }
    };
    docViewer.removeEventListener('dblClick', dblClick);
    docViewer.addEventListener('dblClick', dblClick);

    const onMouseLeftDown = (...args: any[]) => {
      if (args[0]) {
        this.mouseLeftDown.emit();

        /* related ASG-1019 */
        const anot = annotManager.getAnnotationByMouseEvent(args[0]);
        if (anot && isAnnotationType(anot, [AnnotationTypes.FreeText, AnnotationTypes.StickyNote])) {
          // asgInst.viewerAPI.disableTextSelection();
          asgInst.viewerAPI.UI.disableFeatures([asgInst.viewerAPI.UI.Feature.TextSelection]);
        } else {
          // asgInst.viewerAPI.enableTextSelection();
          asgInst.viewerAPI.UI.enableFeatures([asgInst.viewerAPI.UI.Feature.TextSelection]);
        }
      }
    };
    docViewer.removeEventListener('mouseLeftDown', onMouseLeftDown);
    docViewer.addEventListener('mouseLeftDown', onMouseLeftDown);

    const onMouseLeftUp = (...args: any[]) => {
      if (args[0]) {
        this.mouseLeftUp.emit();
      }
    };
    docViewer.removeEventListener('mouseLeftUp', onMouseLeftUp);
    docViewer.addEventListener('mouseLeftUp', onMouseLeftUp);

    const txtSel = (...args: any[]) => {
      if (this.readingModeService.readingMode === ReadingMode.Selection) {
        if (args[0] && args[1] && (args[2] || args[2] === 0)) {
          if (args[0].length) {
            const message = {
              tabId: documentId,
              pageIdx: args[2],
              quads: args[0],
              textLines: mapSelectedTextWithQuads(args[1])
            };
            this.prevSelectedText.next(message);
          }
        }
      }
    };
    docViewer.removeEventListener('textSelected', txtSel);
    docViewer.addEventListener('textSelected', txtSel);

    const mouseOnEv = (...args: any[]) => {
      if (this.mouseIn) {
        if (args[0]) {
          if (this.audioMakerService.isActive()) {
            this.mouseInAndMoving.emit();
          } else if (this.readingModeService.readingMode === ReadingMode.Hover) {
            this.mouseHoverText.next(args[0]);
          } else {
            const annotation = annotManager.getAnnotationByMouseEvent(args[0]);
            this.mouseHoverTextNote.next(annotation);

            if (annotation) {
              const selectedAnnots = annotManager.getSelectedAnnotations();

              if (selectedAnnots && selectedAnnots.length === 1 && annotation.Id === selectedAnnots[0].Id) {
                this.textNoteMoving.next(annotation);
              }
            }
          }
        }
      }
    };
    docViewer.removeEventListener('mouseMove', mouseOnEv);
    docViewer.addEventListener('mouseMove', mouseOnEv);

    docViewer.addEventListener('mouseEnter', () => {
      this.mouseIn = true;
    });
    docViewer.addEventListener('mouseLeave', () => {
      this.mouseIn = false;
    });

    /* annotation selected */
    const annotationSelected = (annotationList: Core.Annotations.Annotation[],
      action: PdfAnnotationActions.Selected | PdfAnnotationActions.Deselected) => {
      if (annotationList && annotationList.length !== 0) {
        this.selectedAnnots = annotationList.map(a => {
          return annotManager.getAnnotationCopy(a, { copyAssociatedLink: true });
        });
      } else {
        this.selectedAnnots = annotationList;
      }

      if (action === PdfAnnotationActions.Selected) {
        this.annotationSelected.emit({ tabId: documentId, annotations: annotationList });
      }
      if (action === PdfAnnotationActions.Deselected) {
        this.annotationDeselected.emit({ tabId: documentId, annotations: annotationList });
        /**
         * Workaround for the PDFTron issue https://github.com/PDFTron/webviewer-ui/issues/349
         * Selecting a shape when another shape is selected sometimes results in closing AnnotationPopup
         * Seems it will be fixed in newer library version, then this block can be removed
         */
        // setTimeout(() => {
        //   const selectedAnnotations = annotManager.getSelectedAnnotations();
        //   if (selectedAnnotations.length) {
        //     annotManager.selectAnnotations(selectedAnnotations);
        //   }
        // });
      }
    };

    /* freehand annotation events */
    const freeHandAnnotationTool = docViewer.getTool(PdfTools.AnnotationCreateFreeHand);
    const freeHandAnnotationCreated = (_: any, annotation: Core.Annotations.FreeHandAnnotation) => {
      this.freeHandAnnotationCreated.emit({ tabId: documentId, annotations: [annotation] });
    };
    const freeHandAnnotationAdded = (_: any, annotation: Core.Annotations.FreeHandAnnotation) => {
      this.freeHandAnnotationAdded.emit({ tabId: documentId, annotations: [annotation] });
    };

    const annotationChanged = async (annotations: Core.Annotations.Annotation[], action: string) => {
      // Log all the highlighting in Google Analytics
      if (action === PdfAnnotationActions.Added) {
        annotations.forEach(annotation => {
          if (annotation.Author === 'Guest' && annotation.Subject === 'Highlight') {
            const colour = convertPdfColourToName(annotation.Color);
            this.googleAnalyticsService.trackAction(GoogleAnalyticsActions.HIGHLIGHT_ADDED, colour);
          }
        });
      }

      /* skip highlighting commands for now */
      // console.log('annotationChanged ', annotations, 'action: ', action);
      const filtered = annotations.filter(e => filterAnnotationExceptHighlights(e));
      if (filtered.length !== 0) {
        const command = new AnnotationCommand(asgInst.asgardDoc, action as PdfAnnotationActions, filtered, this.selectedAnnots);
        await this.commandInvoker.ExecuteCommand(command);
      }
      switch (action) {
        case PdfAnnotationActions.Added:
          this.annotationAdded.emit({ tabId: documentId, annotations });
          break;

        case PdfAnnotationActions.Modified:
          this.annotationModified.emit({ tabId: documentId, annotations });
          break;

        case PdfAnnotationActions.Deleted:
          this.annotationDeleted.emit({ tabId: documentId, annotations });
          break;

        default: break;
      }
    };

    // in Exam mode check hotkeys
    if (this.examService.inProgress) {
      const hotkeyDetectionHandler = (e: KeyboardEvent) => {
        if (detectHotkey(e)) {
          const hotkey = getHotkeyString(e);
          this.hotkeyService.hotkeyDetected.next({ hotkey, source: HotkeyEventSource.PdfDoc });
        }
      }
      
      docViewer.removeEventListener('keyDown', hotkeyDetectionHandler);
      docViewer.addEventListener('keyDown', hotkeyDetectionHandler);
    }

    docViewer.addEventListener('beginRendering', () => window.focus());

    docViewer.addEventListener('documentUnloaded', () => {
      freeHandAnnotationTool.removeEventListener('annotationCreated', freeHandAnnotationCreated);
      freeHandAnnotationTool.removeEventListener('annotationAdded', freeHandAnnotationAdded);
      annotManager.removeEventListener('annotationSelected', annotationSelected);
      annotManager.removeEventListener('annotationChanged', annotationChanged);
    });
  }

  private initServiceSubscrs(): void {
    this.docMgrService.documentRemoved.subscribe(x => {
      this.destroy(x);
    });

    this.settingsService.getSettingsObservable().subscribe((i: ISettings) => {
      if (this.settings && i && this.settings.readingUnit !== i.readingUnit) {
        const doc = this.docMgrService.currentDoc;

        if (doc && doc instanceof AsgardDocPdf) {
          doc.refreshReadingItem();
        }
      }

      if (this.settings && i && this.settings.pageNavigationMode !== i.pageNavigationMode) {
        const doc = this.docMgrService.currentDoc;
        if (doc && doc instanceof AsgardDocPdf) {
          doc.handleCurrentNavigationMode(i.pageNavigationMode);
        }
      }

      this.settings = i;
    });

    this.themeService.ThemeChanged.subscribe((e: LightDarkThemes) => this.setInstancesThemes(e));

    this.paneService.ChangePandeIndexForTabsEv.subscribe((changed: boolean) => {
      if (changed) {
        this.instanceStorage.forEach(e => {
          if (e.paneIndex === 1) {
            e.paneIndex = 0;
          }
        });
      }
    });

    const obsMouse = this.mouseHoverText.asObservable();
    obsMouse.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
    ).subscribe(
      (e: MouseEvent) => {
        if (e) {
          if (e.clientX > 0 && e.clientY > 0) {
            this.wordHovered.emit(e);
          }
        }
      }
    );
  }

  private destroy(documentId: string | null): void {
    if (documentId === null) {
      return;
    }
    const asgInst = this.instanceStorage.get(documentId);

    if (!asgInst) {
      return;
    }

    if (asgInst.instSubscr) {
      asgInst.instSubscr.unsubscribe();
    }
    this.instanceStorage.delete(documentId);
    console.log('destroy called for', documentId);
  }

  public getAllPdfInstancesIDs(): string[] {
    let result: string[] = [];
    this.instanceStorage.forEach((_instance: PdfViewerInstance, id: string) => {
      result.push(id);
    });
    return result;
  }

  checkZoomLevel(tabId: string) {
    const inst = this.getViewerInstance(tabId);
    if (!inst) return; // if instance not found, no point in continuing

    // check the current layout mode
    // only continue if 'dual page'
    const layoutMode = inst.UI.getLayoutMode();
    if (layoutMode !== PdfViewerLayoutModes.FacingContinuous) return;

    const docViewer = this.getDocViewer(tabId);
    if (!docViewer) return // if docViewer not found, no point in continuing

    // check the current zoom level
    const zoomLevel = docViewer?.getZoomLevel();

    // if the zoomLevel is 0, set the one from the settings
    if (zoomLevel === 0 && this.settings && this.settings.savedZoom && this.settings.savedZoom > 0) {
      console.log('Zoom level was 0, zoom to zoom value in settings');
      docViewer.zoomTo(this.settings.savedZoom / 100);
    }
  }
}
